import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Drawer, IconButton, Typography} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {RichTextEditor} from '@/components/common';
import {
    attachmentService,
    type AttachmentVO,
} from '@/services/auditService/attachment';
import {clearAttachmentPreviewCache} from '@/pages/components/audit/adjustment/adjust/support/AttachmentIconButton';

export interface RichTextEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    targetType: string;
    targetId: number;
    userId: number;
    companyId?: number;
    annualId?: number;
    itemName?: string;
    /**
     * Optional controlled override. When omitted, Drawer loads/saves via
     * `/minio_attachments/rich-text` associated with targetType + targetId.
     */
    value?: string;
    onChange?: (next: string) => void;
    readOnly?: boolean;
}

function richtextImageTargetType(attachmentTargetType: string): string {
    return `richtext_${attachmentTargetType}`;
}

/**
 * Drawer shell around RichTextEditor for FsAdjustment attachmentIcon entry.
 * Rich-text HTML is persisted to backend linked by the same targetType/targetId as attachments.
 * Bottom RichTextAttachmentPanel lists historical MinIO attachments.
 */
export default function RichTextEditorDrawer({
    open,
    onClose,
    targetType,
    targetId,
    userId,
    companyId,
    annualId,
    itemName,
    value,
    onChange,
    readOnly = false,
}: RichTextEditorDrawerProps): React.ReactElement {
    const controlled = onChange != null;
    const [draft, setDraft] = useState('');
    const [baseline, setBaseline] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const loadSeqRef = useRef(0);

    const canPersist = targetId > 0 && Boolean(targetType);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setSuccess(null);

        if (controlled) {
            const next = value ?? '';
            setDraft(next);
            setBaseline(next);
            return;
        }

        if (!canPersist) {
            setDraft('');
            setBaseline('');
            return;
        }

        const seq = ++loadSeqRef.current;
        setLoading(true);
        void (async () => {
            try {
                const vo = await attachmentService.getAttachmentRichText(targetType, targetId);
                if (loadSeqRef.current !== seq) return;
                const content = vo.content ?? '';
                setDraft(content);
                setBaseline(content);
            } catch (e) {
                if (loadSeqRef.current !== seq) return;
                setDraft('');
                setBaseline('');
                setError(e instanceof Error ? e.message : 'Failed to load notes');
            } finally {
                if (loadSeqRef.current === seq) {
                    setLoading(false);
                }
            }
        })();
    }, [open, targetType, targetId, controlled, value, canPersist]);

    const richTextValue = controlled ? (value ?? draft) : draft;

    const handleChange = useCallback((next: string) => {
        setDraft(next);
        onChange?.(next);
        setSuccess(null);
    }, [onChange]);

    const dirty = draft !== baseline;

    const persist = useCallback(async (): Promise<boolean> => {
        if (readOnly) return true;
        if (!canPersist) {
            setError('Please save the record first before saving notes.');
            return false;
        }
        if (!dirty && !controlled) {
            return true;
        }
        setSaving(true);
        setError(null);
        try {
            const vo = await attachmentService.saveAttachmentRichText({
                targetType,
                targetId,
                content: draft,
                userId,
                companyId,
                annualId,
            });
            const saved = vo.content ?? draft;
            setDraft(saved);
            setBaseline(saved);
            setSuccess('Notes saved.');
            return true;
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to save notes');
            return false;
        } finally {
            setSaving(false);
        }
    }, [
        readOnly,
        canPersist,
        dirty,
        controlled,
        targetType,
        targetId,
        draft,
        userId,
        companyId,
        annualId,
    ]);

    const uploadContext = useMemo(() => ({
        targetType: richtextImageTargetType(targetType),
        attachmentTargetType: targetType,
        targetId,
        userId,
        companyId,
        annualId,
    }), [targetType, targetId, userId, companyId, annualId]);

    const handleAttachmentsChange = useCallback((_items: AttachmentVO[]) => {
        clearAttachmentPreviewCache(targetType, targetId);
    }, [targetType, targetId]);

    const handleClose = useCallback(async () => {
        if (!readOnly && canPersist && dirty) {
            const ok = await persist();
            if (!ok) return;
        }
        clearAttachmentPreviewCache(targetType, targetId);
        onClose();
    }, [readOnly, canPersist, dirty, persist, targetType, targetId, onClose]);

    const drawerTitle = itemName
        ? `${itemName} Attachment / Notes`
        : 'Attachment / Notes';

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => {
                void handleClose();
            }}
            sx={{zIndex: (theme) => theme.zIndex.modal + 1}}
            PaperProps={{
                sx: {
                    width: {xs: '100%', sm: 600, md: 720},
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                <Typography variant="h6">{drawerTitle}</Typography>
                <IconButton
                    onClick={() => {
                        void handleClose();
                    }}
                    size="small"
                    aria-label="close"
                    disabled={saving}
                >
                    <CloseOutlinedIcon/>
                </IconButton>
            </Box>

            <Box sx={{flex: 1, overflow: 'auto', px: 2, py: 2}}>
                {error && (
                    <Alert severity="error" sx={{mb: 2}} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccess(null)}>
                        {success}
                    </Alert>
                )}
                {!canPersist && (
                    <Alert severity="info" sx={{mb: 2}}>
                        Save the record first to enable notes and attachments.
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                        <CircularProgress size={28}/>
                    </Box>
                ) : (
                    <RichTextEditor
                        value={richTextValue}
                        onChange={handleChange}
                        format="html"
                        readOnly={readOnly || !canPersist}
                        enableImage
                        enableAttachment
                        uploadContext={uploadContext}
                        onAttachmentsChange={handleAttachmentsChange}
                        minHeight={200}
                        placeholder="Enter notes..."
                    />
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                }}
            >
                {!readOnly && (
                    <Button
                        variant="contained"
                        disabled={saving || loading || !canPersist || !dirty}
                        onClick={() => {
                            void persist();
                        }}
                        startIcon={saving ? <CircularProgress size={14} color="inherit"/> : undefined}
                    >
                        Save Notes
                    </Button>
                )}
                <Button
                    onClick={() => {
                        void handleClose();
                    }}
                    disabled={saving}
                >
                    Close
                </Button>
            </Box>
        </Drawer>
    );
}
